import styled, { keyframes } from 'styled-components';
import { theme } from '../../theme';

const rise = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const SalesSectionWrap = styled.section`
  position: relative;
  padding: 5rem 0 6rem;
  background: linear-gradient(180deg, ${theme.colors.bg} 0%, ${theme.colors.bgSecondary} 100%);
  border-top: 1px solid rgba(148, 163, 184, 0.08);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(${theme.colors.primaryRgb}, 0.12), transparent 24%),
      linear-gradient(rgba(148, 163, 184, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.04) 1px, transparent 1px);
    background-size: auto, 60px 60px, 60px 60px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.65), transparent 100%);
  }
`;

export const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const Header = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 370px);
  gap: 1.5rem;
  align-items: end;
  margin-bottom: 2.5rem;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const HeaderCopy = styled.div`
  animation: ${rise} 0.55s ease both;
`;

export const SectionTag = styled.span`
  display: inline-flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.42rem 0.76rem;
  border-radius: 999px;
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.22);
  background: rgba(${theme.colors.primaryRgb}, 0.08);
  color: ${theme.colors.primaryLight};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const SectionTitle = styled.h2`
  margin-bottom: 0.9rem;
  font-size: clamp(2rem, 4vw, 3.1rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  line-height: 1.02;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const SectionSubtitle = styled.p`
  max-width: 620px;
  color: ${theme.colors.textMuted};
  font-size: 1rem;
  line-height: 1.78;
`;

export const AsideCard = styled.aside`
  padding: 1.15rem 1.2rem;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));
  box-shadow: 0 18px 34px rgba(2, 6, 23, 0.18);
  animation: ${rise} 0.55s ease 0.08s both;

  span {
    display: inline-flex;
    margin-bottom: 0.45rem;
    color: ${theme.colors.primaryLight};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  h3 {
    margin-bottom: 0.45rem;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.92rem;
    line-height: 1.7;
  }
`;

export const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.9rem;
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const ProcessCard = styled.article`
  padding: 1.1rem;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 12, 18, 0.72);
  animation: ${rise} 0.55s ease both;

  strong {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 1rem;
    font-weight: 700;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.88rem;
    line-height: 1.65;
  }
`;

export const ProofGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 2rem;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const ProofCard = styled.div`
  padding: 1rem 1.05rem;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));
  animation: ${rise} 0.55s ease both;

  span {
    display: block;
    margin-bottom: 0.35rem;
    color: ${theme.colors.textMuted};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.92rem;
    line-height: 1.5;
  }
`;

export const FaqSection = styled.div`
  display: grid;
  gap: 0.8rem;
`;

export const FaqTitle = styled.h3`
  margin-bottom: 0.1rem;
  font-size: clamp(1.45rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.05em;
  color: ${theme.colors.text};
`;

export const FaqItem = styled.details`
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(8, 12, 18, 0.72);
  overflow: hidden;
  animation: ${rise} 0.55s ease both;

  &[open] {
    border-color: rgba(${theme.colors.primaryRgb}, 0.22);
  }
`;

export const FaqQuestion = styled.summary`
  padding: 1rem 1.05rem;
  list-style: none;
  cursor: pointer;
  color: ${theme.colors.text};
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.96rem;
  font-weight: 700;

  &::-webkit-details-marker {
    display: none;
  }
`;

export const FaqAnswer = styled.p`
  padding: 0 1.05rem 1rem;
  color: ${theme.colors.textMuted};
  font-size: 0.92rem;
  line-height: 1.72;
`;